/**
 * EU Tachograph Stop Calculator
 * Based on Regulation (EC) No 561/2006
 *
 * DAILY CYCLE (from Excel "mapa de horas de trabalho para camiões"):
 * ┌─────────┬───────┬─────────┬───────┬─────────┬──────────────┐
 * │ Cond 1  │ Pausa │ Cond 2  │ Pausa │ Cond 3  │ Descanso 11h │
 * │ 4h30    │ 45min │ 4h30    │ 45min │ 2h30    │ 11h          │
 * └─────────┴───────┴─────────┴───────┴─────────┴──────────────┘
 * Total condução: 11h30 (690min) | Total pausa: 1h30 (90min) | Total: 24h
 *
 * WEEKLY CYCLE:
 * - Max condução semanal: 56h (3360min) — normal limit
 * - Max condução bisemanal: 90h (5400min)
 * - Descanso semanal: 45h (2700min) seguido — pode ser reduzido a 24h 1x/2 semanas
 *
 * SPLIT BREAKS allowed:
 * - 45min can be split: first part ≥15min, second part ≥30min
 * - Must total 45min within 4h30 driving window
 *
 * DAILY REST:
 * - Normal: 11h continuous (660min)
 * - Reduced: 9h (540min) — max 3x between weekly rests
 * - Split: 3h + 9h = 12h total (first part ≥3h, second ≥9h)
 */

// ── Driving limits ──
const MAX_CONTINUOUS_DRIVE_MIN = 270;    // 4h30 — max before mandatory break
const BREAK_DURATION_MIN = 45;           // mandatory break duration
const MAX_DAILY_DRIVE_MIN = 540;         // 9h normal daily limit
const MAX_DAILY_DRIVE_EXTENDED_MIN = 600;// 10h — allowed 2x per week
const DAILY_REST_MIN = 660;              // 11h mandatory daily rest
const DAILY_REST_REDUCED_MIN = 540;      // 9h reduced daily rest (max 3x between weekly rests)
const MAX_WEEKLY_DRIVE_MIN = 3360;       // 56h weekly driving limit
const WEEKLY_REST_MIN = 2700;            // 45h weekly rest
const WEEKLY_REST_REDUCED_MIN = 1440;    // 24h reduced weekly rest (compensate within 3 weeks)

// ── Daily driving pattern from Excel ──
// Period 1: 270min drive → 45min break
// Period 2: 270min drive → 45min break
// Period 3: 150min drive → 660min daily rest
const DAILY_DRIVE_PATTERN = [
  { drive: 270, breakAfter: BREAK_DURATION_MIN, type: 'break_45', label: 'Pausa obrigatória' },
  { drive: 270, breakAfter: BREAK_DURATION_MIN, type: 'break_45', label: 'Pausa obrigatória' },
  { drive: 150, breakAfter: DAILY_REST_MIN,     type: 'daily_rest', label: 'Descanso diário' },
];
// Total driving per day: 270+270+150 = 690min (11h30)
// But legal max is 9h (540min) normal / 10h (600min) extended
// The 3rd period is only possible if using the 10h extended day allowance

/**
 * Calculate mandatory rest stops for a route
 * @param {number} totalDurationSec - Total route duration in seconds
 * @param {number} totalDistanceM - Total route distance in meters
 * @param {string|null} departureTime - ISO datetime string
 * @param {Object|null} tachoData - Driver's current tachograph data
 *   { continuousDriveRemainMin, dailyDriveRemainMin, weeklyDriveRemainMin,
 *     extendedDaysUsed, reducedRestsUsed }
 * @param {Array|null} restAreas - Rest areas along route: [{ name, lat, lon, distAlongRouteKm, preferred }]
 * @param {Array|null} waypointStops - Waypoint stop durations: [{ km, duration }]
 * @returns {{ stops: Array, summary: Object }}
 */
export function calculateStops(totalDurationSec, totalDistanceM, departureTime = null, tachoData = null, restAreas = null, waypointStops = null) {
  const totalDriveMin = Math.round(totalDurationSec / 60);
  const totalKm = totalDistanceM / 1000;
  const avgSpeedKmMin = totalKm / totalDriveMin; // km per minute of driving
  const stops = [];

  if (totalDriveMin <= 0 || totalKm <= 0) return stops;

  // ── Initialize from tachoData (driver's current state) ──
  let continuousDriven = 0;
  let dailyDriven = 0;
  let weeklyDriven = 0;
  let periodInDay = 0; // which driving period we're in (0, 1, 2)
  let extendedDaysUsed = 0; // max 2 per week
  let reducedRestsUsed = 0; // max 3 between weekly rests

  if (tachoData) {
    // How much has the driver already driven continuously
    if (tachoData.continuousDriveRemainMin != null && tachoData.continuousDriveRemainMin >= 0) {
      continuousDriven = MAX_CONTINUOUS_DRIVE_MIN - tachoData.continuousDriveRemainMin;
    }
    // How much has the driver already driven today
    if (tachoData.dailyDriveRemainMin != null && tachoData.dailyDriveRemainMin >= 0) {
      dailyDriven = MAX_DAILY_DRIVE_MIN - tachoData.dailyDriveRemainMin;
    }
    // Weekly totals
    if (tachoData.weeklyDriveRemainMin != null && tachoData.weeklyDriveRemainMin >= 0) {
      weeklyDriven = MAX_WEEKLY_DRIVE_MIN - tachoData.weeklyDriveRemainMin;
    }
    if (tachoData.extendedDaysUsed != null) extendedDaysUsed = tachoData.extendedDaysUsed;
    if (tachoData.reducedRestsUsed != null) reducedRestsUsed = tachoData.reducedRestsUsed;

    // Determine which period the driver is in based on daily driven
    if (dailyDriven >= 540) {
      periodInDay = 2; // already in 3rd period or exceeded
    } else if (dailyDriven >= 270) {
      periodInDay = 1; // in 2nd period
    }
  }

  // ── Waypoint stops map (km → duration) for deducting from driving time ──
  const wpStopMap = new Map();
  if (waypointStops) {
    waypointStops.forEach(ws => {
      if (ws.km != null && ws.duration > 0) {
        wpStopMap.set(Math.round(ws.km), ws.duration);
      }
    });
  }

  let drivenMinTotal = 0;  // total driving minutes consumed along route
  let currentKm = 0;
  let currentTime = departureTime ? new Date(departureTime) : null;
  const usedRestAreaKeys = new Set();

  while (drivenMinTotal < totalDriveMin) {
    const remainingDriveMin = totalDriveMin - drivenMinTotal;

    // Current period's max continuous driving
    const pattern = DAILY_DRIVE_PATTERN[periodInDay] || DAILY_DRIVE_PATTERN[2];
    const continuousRemain = pattern.drive - continuousDriven;

    // Daily driving limit for current day
    const canExtend = extendedDaysUsed < 2;
    const dailyLimit = canExtend ? MAX_DAILY_DRIVE_EXTENDED_MIN : MAX_DAILY_DRIVE_MIN;
    const dailyRemain = dailyLimit - dailyDriven;

    // Weekly limit
    const weeklyRemain = MAX_WEEKLY_DRIVE_MIN - weeklyDriven;

    // The next driving segment is the minimum of all limits
    let segmentDrive = Math.min(continuousRemain, dailyRemain, weeklyRemain, remainingDriveMin);

    if (segmentDrive <= 0) {
      // Can't drive any more — need rest
      // If daily limit reached → daily rest
      // If weekly limit reached → weekly rest
      if (weeklyRemain <= 0) {
        // Weekly rest needed
        _addStop(stops, currentKm, totalKm, drivenMinTotal, totalDriveMin,
          WEEKLY_REST_MIN, 'weekly_rest', 'Descanso semanal',
          currentTime, restAreas, usedRestAreaKeys, avgSpeedKmMin);
        if (currentTime) currentTime = new Date(currentTime.getTime() + WEEKLY_REST_MIN * 60000);
        weeklyDriven = 0;
        dailyDriven = 0;
        continuousDriven = 0;
        periodInDay = 0;
        extendedDaysUsed = 0;
        reducedRestsUsed = 0;
        continue;
      }
      // Daily rest
      const restDuration = reducedRestsUsed < 3 ? DAILY_REST_REDUCED_MIN : DAILY_REST_MIN;
      const restType = restDuration < DAILY_REST_MIN ? 'daily_rest_reduced' : 'daily_rest';
      _addStop(stops, currentKm, totalKm, drivenMinTotal, totalDriveMin,
        restDuration, restType, restDuration < DAILY_REST_MIN ? 'Descanso diário reduzido (9h)' : 'Descanso diário (11h)',
        currentTime, restAreas, usedRestAreaKeys, avgSpeedKmMin);
      if (currentTime) currentTime = new Date(currentTime.getTime() + restDuration * 60000);
      if (restDuration < DAILY_REST_MIN) reducedRestsUsed++;
      dailyDriven = 0;
      continuousDriven = 0;
      periodInDay = 0;
      if (dailyLimit > MAX_DAILY_DRIVE_MIN) extendedDaysUsed++;
      continue;
    }

    // Drive this segment
    drivenMinTotal += segmentDrive;
    continuousDriven += segmentDrive;
    dailyDriven += segmentDrive;
    weeklyDriven += segmentDrive;
    currentKm = Math.round(totalKm * (drivenMinTotal / totalDriveMin));
    if (currentTime) currentTime = new Date(currentTime.getTime() + segmentDrive * 60000);

    // Check if we still have driving to do
    if (drivenMinTotal >= totalDriveMin) break;

    // Skip break (not rest) if remaining driving is very short (≤15min) and within daily limit
    const afterSegmentRemain = totalDriveMin - drivenMinTotal;
    const canExtendNow = extendedDaysUsed < 2;
    const currentDayLimit = canExtendNow ? MAX_DAILY_DRIVE_EXTENDED_MIN : MAX_DAILY_DRIVE_MIN;
    if (afterSegmentRemain <= 15 && (dailyDriven + afterSegmentRemain) <= currentDayLimit) break;

    // Determine what break/rest is needed
    if (continuousDriven >= pattern.drive) {
      // End of driving period — check what comes next
      if (dailyDriven >= dailyLimit || periodInDay >= 2) {
        // Daily driving limit reached → daily rest
        const restDuration = (reducedRestsUsed < 3 && dailyDriven <= MAX_DAILY_DRIVE_MIN)
          ? DAILY_REST_REDUCED_MIN : DAILY_REST_MIN;
        const restType = restDuration < DAILY_REST_MIN ? 'daily_rest_reduced' : 'daily_rest';
        _addStop(stops, currentKm, totalKm, drivenMinTotal, totalDriveMin,
          restDuration, restType,
          restDuration < DAILY_REST_MIN ? 'Descanso diário reduzido (9h)' : 'Descanso diário (11h)',
          currentTime, restAreas, usedRestAreaKeys, avgSpeedKmMin);
        if (currentTime) currentTime = new Date(currentTime.getTime() + restDuration * 60000);
        if (restDuration < DAILY_REST_MIN) reducedRestsUsed++;
        if (dailyDriven > MAX_DAILY_DRIVE_MIN) extendedDaysUsed++;
        dailyDriven = 0;
        continuousDriven = 0;
        periodInDay = 0;
      } else {
        // Mandatory 45min break between driving periods
        _addStop(stops, currentKm, totalKm, drivenMinTotal, totalDriveMin,
          BREAK_DURATION_MIN, 'break_45', 'Pausa obrigatória (45min)',
          currentTime, restAreas, usedRestAreaKeys, avgSpeedKmMin);
        if (currentTime) currentTime = new Date(currentTime.getTime() + BREAK_DURATION_MIN * 60000);
        continuousDriven = 0;
        periodInDay++;
      }
    }
  }

  // Add tachoAdjusted flag to first stop if driver had prior driving
  if (stops.length > 0 && tachoData && tachoData.continuousDriveRemainMin != null
      && tachoData.continuousDriveRemainMin < MAX_CONTINUOUS_DRIVE_MIN) {
    stops[0].tachoAdjusted = true;
  }

  return stops;
}

/**
 * Internal: create a stop and snap to nearest rest area
 */
function _addStop(stops, currentKm, totalKm, drivenMinTotal, totalDriveMin,
                  duration, type, label, currentTime, restAreas, usedRestAreaKeys, avgSpeedKmMin) {
  let stopKm = currentKm;
  let suggestedRestArea = null;

  // Snap to rest area (up to 30km BEFORE — never after the limit)
  if (restAreas && restAreas.length > 0) {
    const searchMin = Math.max(0, stopKm - 30);
    const candidates = restAreas.filter(ra =>
      ra.distAlongRouteKm >= searchMin && ra.distAlongRouteKm <= stopKm
      && !usedRestAreaKeys.has(`${ra.lat},${ra.lon}`)
    );
    if (candidates.length > 0) {
      // Prefer preferred areas, then closest to stop point
      candidates.sort((a, b) => {
        if (a.preferred && !b.preferred) return -1;
        if (!a.preferred && b.preferred) return 1;
        return Math.abs(a.distAlongRouteKm - stopKm) - Math.abs(b.distAlongRouteKm - stopKm);
      });
      suggestedRestArea = candidates[0];
      usedRestAreaKeys.add(`${suggestedRestArea.lat},${suggestedRestArea.lon}`);
      stopKm = Math.round(suggestedRestArea.distAlongRouteKm);
    }
  }

  const arrivalTime = currentTime
    ? currentTime.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })
    : null;

  // Arrival date for multi-day routes
  const arrivalDate = currentTime
    ? currentTime.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })
    : null;

  stops.push({
    km: stopKm,
    drivingMinBefore: drivenMinTotal,
    arrivalTime,
    arrivalDate,
    duration,
    type,
    label,
    progressRatio: totalKm > 0 ? stopKm / totalKm : 0,
    suggestedRestArea: suggestedRestArea ? {
      name: suggestedRestArea.name,
      lat: suggestedRestArea.lat,
      lon: suggestedRestArea.lon,
      distAlongRouteKm: suggestedRestArea.distAlongRouteKm,
      preferred: suggestedRestArea.preferred || false,
    } : null,
    tachoAdjusted: false,
  });
}

/**
 * Calculate ETA (estimated time of arrival) considering stops and waypoint durations
 * @param {string} departureTime - ISO datetime
 * @param {number} totalDurationSec - route driving duration in seconds
 * @param {Array} stops - calculated tachograph stops
 * @param {Array|null} waypointStops - waypoint stop durations [{ duration }]
 * @returns {{ eta: Date, totalTravelMin: number, totalStopMin: number }}
 */
export function calculateETA(departureTime, totalDurationSec, stops = [], waypointStops = null) {
  if (!departureTime) return null;

  const driveMin = Math.round(totalDurationSec / 60);
  const tachoStopMin = stops.reduce((sum, s) => sum + (s.duration || 0), 0);
  const wpStopMin = (waypointStops || []).reduce((sum, ws) => sum + (ws.duration || 0), 0);
  const totalMin = driveMin + tachoStopMin + wpStopMin;

  const departure = new Date(departureTime);
  const eta = new Date(departure.getTime() + totalMin * 60000);

  return {
    eta,
    totalTravelMin: totalMin,
    totalDriveMin: driveMin,
    totalStopMin: tachoStopMin + wpStopMin,
  };
}

/**
 * Format driving time for display
 */
export function formatDriveTime(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
}

/**
 * Check if total driving time exceeds daily limit
 */
export function checkDailyLimit(totalDrivingMin) {
  return {
    exceeds: totalDrivingMin > MAX_DAILY_DRIVE_MIN,
    remaining: Math.max(0, MAX_DAILY_DRIVE_MIN - totalDrivingMin),
    percentage: Math.min(100, (totalDrivingMin / MAX_DAILY_DRIVE_MIN) * 100),
  };
}

/**
 * Get a summary of the driving plan
 */
export function getDrivingSummary(stops, totalDriveMin) {
  const breaks45 = stops.filter(s => s.type === 'break_45');
  const dailyRests = stops.filter(s => s.type.startsWith('daily_rest'));
  const weeklyRests = stops.filter(s => s.type === 'weekly_rest');
  const drivingDays = dailyRests.length + 1;

  return {
    totalDriveMin,
    totalDriveFormatted: formatDriveTime(totalDriveMin),
    totalStops: stops.length,
    breaks: breaks45.length,
    dailyRests: dailyRests.length,
    weeklyRests: weeklyRests.length,
    drivingDays,
    totalBreakMin: stops.reduce((s, st) => s + st.duration, 0),
  };
}
